import { useState, useEffect } from "react";
import { useParams, Navigate, Link, useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { CTA } from "../components/Sections";
import { Reveal } from "../components/motion";
import { BrowserMock, PhoneMock } from "../components/Visuals";
import {
  PROJECTS,
  TESTIMONIALS,
  LEGAL_ENTITY,
  SECTOR_LABELS,
  CLIENTS,
} from "../data";
import {
  ArrowRight,
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  ShieldCheck,
  ExternalLink,
  Sparkles,
  Layers,
  Cpu,
  Globe,
  Star,
  Check,
  Building2,
  Calendar,
  Zap,
  TrendingUp,
  FileText,
} from "lucide-react";
import { openConsultationModal } from "../components/ConsultationModal";
import api from "../lib/api";

const EXCLUDED_PROJECT_SLUGS = ["woodwelt", "mycitydekho", "digital-india-rural-iot"];

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [projectList, setProjectList] = useState(() =>
    PROJECTS.filter((p) => !EXCLUDED_PROJECT_SLUGS.includes(p.slug))
  );

  const normalizedSlug = (slug || "").toLowerCase().trim();

  useEffect(() => {
    api
      .get("/projects")
      .then((res) => {
        if (res.data && res.data.length > 0) {
          setProjectList(() => {
            const merged = PROJECTS.filter((p) => !EXCLUDED_PROJECT_SLUGS.includes(p.slug));
            res.data.forEach((p) => {
              if (EXCLUDED_PROJECT_SLUGS.includes(p.slug)) return;
              const idx = merged.findIndex((m) => m.slug === p.slug);
              if (idx >= 0) merged[idx] = { ...merged[idx], ...p };
              else merged.push(p);
            });
            return merged.filter((p) => !EXCLUDED_PROJECT_SLUGS.includes(p.slug));
          });
        }
      })
      .catch(() => {});
  }, []);

  // Check static PROJECTS first as the primary single source of truth
  const project =
    PROJECTS.find((p) => p.slug === normalizedSlug || (normalizedSlug === "medylife" && p.slug === "meadylife")) ||
    projectList.find((p) => p.slug === normalizedSlug || (normalizedSlug === "medylife" && p.slug === "meadylife"));

  if (!project) return <Navigate to="/portfolio" replace />;

  const currentIndex = PROJECTS.findIndex((p) => p.slug === project.slug);
  const nextProject =
    PROJECTS[(currentIndex + 1) % PROJECTS.length] || PROJECTS[0];
  const prevProject =
    PROJECTS[(currentIndex - 1 + PROJECTS.length) % PROJECTS.length] ||
    PROJECTS[PROJECTS.length - 1];

  const related = PROJECTS.filter(
    (p) => p.slug !== project.slug && (p.sector === project.sector || p.cat === project.cat)
  ).slice(0, 3);

  // Match client testimonial if available
  const clientReview = TESTIMONIALS.find(
    (t) =>
      t.company.toLowerCase() === project.title.toLowerCase() ||
      t.company.toLowerCase() === (project.domain || "").toLowerCase().replace(/\.[a-z]+$/, "")
  );

  const clientMatch = CLIENTS.find(
    (c) => c.name.toLowerCase() === project.title.toLowerCase()
  );
  const clientLogo = project.logo || clientMatch?.logo;

  const schemaType =
    project.cat === "Mobile" || project.cat === "Web"
      ? "SoftwareApplication"
      : "CreativeWork";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": schemaType,
    name: `${project.title} Case Study`,
    headline: project.tagline
      ? `${project.title}: ${project.tagline}`
      : `${project.title} Case Study`,
    description: project.desc || project.overview,
    url: `https://neuravolt.in/portfolio/${project.slug}`,
    ...(project.img ? { image: project.img } : {}),
    creator: {
      "@type": "Organization",
      name: "NeuraVolt Technology",
      legalName: LEGAL_ENTITY,
      url: "https://neuravolt.in",
    },
    provider: {
      "@type": "Organization",
      name: "NeuraVolt Technology",
      legalName: LEGAL_ENTITY,
      url: "https://neuravolt.in",
    },
    ...(project.sector ? { about: { "@type": "Thing", name: project.sector } } : {}),
    ...(schemaType === "SoftwareApplication" && project.cat
      ? { applicationCategory: `${project.cat}Application` }
      : {}),
    ...(project.tech && project.tech.length > 0
      ? {
          keywords: Array.isArray(project.tech)
            ? project.tech.join(", ")
            : project.tech,
        }
      : {}),
    ...(project.live_url ? { sameAs: project.live_url } : {}),
  };

  const isComingSoon = project.challenge === "Coming Soon";
  const isPHP2Shell = project.slug === "php2shell";

  return (
    <Layout>
      <Seo
        title={`${project.title} · Case Study`}
        description={project.desc || project.overview}
        path={`/portfolio/${project.slug}`}
        image={project.img}
        jsonLd={jsonLd}
      />

      {/* Top Header & Breadcrumb Ribbon */}
      <section
        className={`px-5 sm:px-8 border-b border-white/5 relative overflow-hidden ${
          isPHP2Shell
            ? "bg-[#0B0806] pt-20 sm:pt-24 pb-6 sm:pb-8"
            : "mesh-dark pt-28 sm:pt-36 pb-6"
        }`}
      >
        {isPHP2Shell ? (
          <>
            {/* Ambient orange glow in top-left and spotlight on right */}
            <div className="absolute -top-28 -left-28 w-[450px] h-[450px] bg-[#FF5A1F]/12 rounded-full blur-[130px] pointer-events-none" />
            <div className="absolute top-1/4 -right-10 w-[480px] h-[480px] bg-[#FF5A1F]/18 rounded-full blur-[140px] pointer-events-none" />
          </>
        ) : (
          <div className="blob w-96 h-96 bg-[#FF5A1F] -top-32 -left-32 animate-aurora pointer-events-none" style={{ opacity: 0.2 }} />
        )}
        
        <div className="max-w-6xl mx-auto relative z-10">
          {/* Breadcrumb Navigation (rendered for standard projects) */}
          {!isPHP2Shell && (
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8 sm:mb-12">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-neutral-400 font-mono">
                <Link to="/portfolio" className="hover:text-white transition-colors flex items-center gap-1.5">
                  <ArrowLeft className="w-3.5 h-3.5 text-[#FF5A1F]" />
                  <span>All Projects</span>
                </Link>
                <span className="text-neutral-600">/</span>
                {project.sector && (
                  <>
                    <Link
                      to={`/industries/${project.sector}`}
                      className="hover:text-white transition-colors capitalize text-neutral-400"
                    >
                      {SECTOR_LABELS[project.sector] || project.sector}
                    </Link>
                    <span className="text-neutral-600">/</span>
                  </>
                )}
                <span className="text-white font-medium truncate max-w-[200px]">{project.title}</span>
              </div>
            </div>
          )}

          {isPHP2Shell ? (
            /* Custom 2-Column Showcase Header for PHP2Shell: Sleek, Ultra-Minimalist & Compact */
            <Reveal>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
                {/* Left Column: Title and Subheading */}
                <div className="lg:col-span-6 space-y-3 sm:space-y-4">
                  <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.6rem] font-bold tracking-tight text-white leading-[1.06]">
                    <span>PHP2</span><span className="text-[#FF5A1F]">Shell</span>
                  </h1>

                  <p className="text-sm sm:text-base lg:text-lg text-neutral-300 font-normal leading-relaxed max-w-md">
                    {project.tagline || "Runtime Vulnerability Analysis & Infrastructure Hardening"}
                  </p>
                </div>

                {/* Right Column: Clean 3D Laptop on Pedestal with Ambient Glow */}
                <div className="lg:col-span-6 relative flex items-center justify-center lg:justify-end">
                  <div className="relative w-full max-w-[460px] lg:max-w-[490px]">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] h-[280px] bg-[#FF5A1F]/18 rounded-full blur-[90px] pointer-events-none" />
                    <img
                      src="/brand/php2shell-hero-laptop.png"
                      alt="PHP2Shell Terminal Environment"
                      className="w-full h-auto object-contain drop-shadow-[0_16px_45px_rgba(0,0,0,0.85)] select-none pointer-events-none"
                    />
                  </div>
                </div>
              </div>

              {/* 3-Column Minimalist Metadata Ribbon */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6 mt-8 sm:mt-10 pt-5 sm:pt-6 border-t border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#FF5A1F]/10 border border-[#FF5A1F]/20 grid place-items-center shrink-0">
                    <FileText className="w-4 h-4 text-[#FF5A1F]" />
                  </div>
                  <div>
                    <div className="text-[10px] sm:text-[11px] font-mono text-neutral-400 uppercase tracking-wider">
                      CLIENT / PRODUCT
                    </div>
                    <div className="font-heading font-semibold text-white text-xs sm:text-sm mt-0.5">
                      PHP2Shell
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#FF5A1F]/10 border border-[#FF5A1F]/20 grid place-items-center shrink-0">
                    <Globe className="w-4 h-4 text-[#FF5A1F]" />
                  </div>
                  <div>
                    <div className="text-[10px] sm:text-[11px] font-mono text-neutral-400 uppercase tracking-wider">
                      INDUSTRY
                    </div>
                    <div className="font-heading font-semibold text-white text-xs sm:text-sm capitalize mt-0.5">
                      {SECTOR_LABELS[project.sector] || project.sector || "Cybersecurity"}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#FF5A1F]/10 border border-[#FF5A1F]/20 grid place-items-center shrink-0">
                    <Layers className="w-4 h-4 text-[#FF5A1F]" />
                  </div>
                  <div>
                    <div className="text-[10px] sm:text-[11px] font-mono text-neutral-400 uppercase tracking-wider">
                      DELIVERABLE
                    </div>
                    <div className="font-heading font-semibold text-white text-xs sm:text-sm mt-0.5">
                      {project.cat ? `${project.cat} Application` : "Security Application"}
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          ) : (
            /* Standard Header for Other Projects */
            <>
              {/* Title & Eyebrow */}
              <Reveal>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.08] mb-4">
                  {project.title}
                </h1>

                {project.tagline && (
                  <p className="font-heading text-lg sm:text-2xl font-medium text-neutral-300 max-w-4xl leading-snug mb-6">
                    {project.tagline}
                  </p>
                )}

                <p className="text-neutral-400 text-base sm:text-lg leading-relaxed max-w-3xl">
                  {project.desc || project.overview}
                </p>
              </Reveal>

              {/* Metadata Ribbon */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10 pt-8 border-t border-white/10">
                <div>
                  <div className="text-[11px] font-mono text-neutral-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <Building2 className="w-3 h-3 text-[#FF5A1F]" /> Client / Product
                  </div>
                  <div className="font-heading font-semibold text-white text-sm sm:text-base">
                    {project.title}
                  </div>
                </div>

                <div>
                  <div className="text-[11px] font-mono text-neutral-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <Globe className="w-3 h-3 text-[#FF5A1F]" /> Industry
                  </div>
                  <div className="font-heading font-semibold text-white text-sm sm:text-base capitalize">
                    {SECTOR_LABELS[project.sector] || project.sector || "Enterprise"}
                  </div>
                </div>

                <div>
                  <div className="text-[11px] font-mono text-neutral-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <Layers className="w-3 h-3 text-[#FF5A1F]" /> Deliverable
                  </div>
                  <div className="font-heading font-semibold text-white text-sm sm:text-base">
                    {project.cat ? `${project.cat} Application` : "Custom Software"}
                  </div>
                </div>

                <div className="flex flex-col justify-center">
                  {project.domain ? (
                    <a
                      href={project.live_url || `https://${project.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[#FF5A1F] hover:bg-[#ff6d38] text-white font-semibold text-xs sm:text-sm transition-all shadow-lg shadow-[#FF5A1F]/20 hover:-translate-y-0.5"
                    >
                      <span>Visit Live Site</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <span className="text-xs font-mono text-neutral-400">Enterprise Private</span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Main Showcase Stage */}
      <section className="py-12 sm:py-20 px-5 sm:px-8 mesh-light">
        <div className="max-w-6xl mx-auto space-y-14">
          {/* Framed Device Mockup with Aurora Glow */}
          <Reveal>
            <div className="relative group">
              <div
                className="absolute inset-0 bg-gradient-to-r from-[#FF5A1F]/20 via-orange-500/10 to-transparent blur-3xl -z-10 rounded-3xl"
                style={{ transform: "scale(0.95)" }}
              />

              <div className="rounded-3xl p-2 sm:p-4 bg-[#0B0B0C] border border-black/10 shadow-[0_30px_70px_-20px_rgba(0,0,0,0.35)] overflow-hidden">
                <BrowserMock
                  src={project.img}
                  alt={`${project.title} Platform Interface`}
                  className="aspect-[16/10] w-full"
                />
              </div>
            </div>
          </Reveal>

          {/* Key Proven Results / KPI Grid */}
          {project.metrics && project.metrics.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-[#FF5A1F]">
                <TrendingUp className="w-4 h-4" />
                <span>Verified Impact & Measurable Outcomes</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {project.metrics.map((m, idx) => (
                  <Reveal key={idx} delay={idx * 0.08}>
                    <div className="h-full rounded-2xl p-6 bg-white border border-black/10 shadow-sm hover:shadow-md hover:border-[#FF5A1F]/40 transition-all flex flex-col justify-between">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-8 h-8 rounded-lg bg-[#FF5A1F]/10 text-[#FF5A1F] grid place-items-center font-mono font-bold text-xs">
                          0{idx + 1}
                        </div>
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      </div>
                      <p className="font-heading text-base sm:text-lg font-bold text-neutral-900 leading-snug">
                        {typeof m === "object" && m !== null
                          ? m.label
                            ? `${m.value ? m.value + " — " : ""}${m.label}`
                            : m.value || ""
                          : m}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          )}

          {/* Two-Column Editorial Deep Dive */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            {/* Left Column: Narrative (Overview, Challenge, Solution, Features) */}
            <div className="lg:col-span-8 space-y-10">
              {/* Executive Overview */}
              <div className="rounded-3xl p-8 sm:p-10 bg-white border border-black/10 shadow-sm">
                <span className="text-xs font-mono font-bold uppercase tracking-wider text-[#FF5A1F] block mb-2">
                  01 / EXECUTIVE CONTEXT
                </span>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-neutral-900 mb-4">
                  About the Engagement
                </h2>
                <p className="text-neutral-600 leading-relaxed text-base sm:text-lg font-normal">
                  {project.overview || project.desc}
                </p>
              </div>

              {/* Challenge Card */}
              <div className="rounded-3xl p-8 sm:p-10 bg-white border border-black/10 shadow-sm">
                <span className="text-xs font-mono font-bold uppercase tracking-wider text-rose-500 block mb-2">
                  02 / THE ARCHITECTURAL CHALLENGE
                </span>
                <h3 className="font-display text-2xl font-bold text-neutral-900 mb-4">
                  The Problem & Bottlenecks
                </h3>
                <p className="text-neutral-600 leading-relaxed text-base">
                  {project.challenge}
                </p>
              </div>

              {/* Engineering Solution */}
              <div className="rounded-3xl p-8 sm:p-10 bg-[#0B0B0C] text-white border border-white/10 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-80 h-80 bg-[#FF5A1F]/15 blur-3xl pointer-events-none rounded-full" />
                
                <div className="relative z-10">
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-[#FF5A1F] block mb-2">
                    03 / THE NEURAVOLT SOLUTION
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-4">
                    Engineered for Production Scale
                  </h3>
                  <p className="text-neutral-300 leading-relaxed text-base sm:text-lg mb-8">
                    {project.solution}
                  </p>

                  {/* Results Highlights */}
                  {project.results && project.results.length > 0 && (
                    <div className="pt-6 border-t border-white/10 space-y-3">
                      <div className="text-xs font-mono uppercase tracking-wider text-neutral-400 font-semibold mb-3">
                        Key Deliverables Shipped:
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {project.results.map((r, i) => (
                          <div key={i} className="flex items-start gap-2.5 text-sm text-neutral-200">
                            <span className="w-5 h-5 rounded-full bg-[#FF5A1F]/20 text-[#FF5A1F] flex items-center justify-center shrink-0 mt-0.5">
                              <Check className="w-3 h-3" />
                            </span>
                            <span>{r}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Structured Security Response & Mitigation Framework */}
              {project.mitigationSteps && project.mitigationSteps.length > 0 && (
                <div className="rounded-3xl p-8 sm:p-10 bg-white border border-black/10 shadow-sm space-y-6">
                  <div>
                    <span className="text-xs font-mono font-bold uppercase tracking-wider text-[#FF5A1F] block mb-2">
                      04 / {project.mitigationEyebrow || "SECURITY RESPONSE & MITIGATION"}
                    </span>
                    <h3 className="font-display text-2xl sm:text-3xl font-bold text-neutral-900 mb-3">
                      {project.mitigationTitle || "Structured Vulnerability Management Lifecycle"}
                    </h3>
                    <p className="text-neutral-600 text-sm sm:text-base leading-relaxed">
                      {project.mitigationSubtitle || `Organizations can systematically reduce their exposure radius through a disciplined, ${project.mitigationSteps.length}-phase defensive engineering lifecycle:`}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                    {project.mitigationSteps.map((step, idx) => (
                      <div key={idx} className="p-5 rounded-2xl bg-neutral-50/80 border border-black/5 hover:border-[#FF5A1F]/30 transition-colors flex flex-col justify-between">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="px-2.5 py-1 rounded-md bg-[#FF5A1F]/10 text-[#FF5A1F] font-mono font-bold text-xs">
                            {step.step}
                          </span>
                          <h4 className="font-heading font-bold text-neutral-900 text-base">
                            {step.name}
                          </h4>
                        </div>
                        <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed">
                          {step.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Core Security Findings & Takeaways */}
              {project.keyFindings && project.keyFindings.length > 0 && (
                <div className="rounded-3xl p-8 sm:p-10 bg-white border border-black/10 shadow-sm space-y-6">
                  <div>
                    <span className="text-xs font-mono font-bold uppercase tracking-wider text-emerald-600 block mb-2">
                      05 / KEY ARCHITECTURAL FINDINGS
                    </span>
                    <h3 className="font-display text-2xl sm:text-3xl font-bold text-neutral-900 mb-4">
                      Strategic Cybersecurity Lessons
                    </h3>
                  </div>

                  <div className="space-y-3.5">
                    {project.keyFindings.map((finding, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-4 rounded-xl bg-neutral-50 border border-black/5 text-sm text-neutral-700 leading-relaxed">
                        <span className="w-5 h-5 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="w-3.5 h-3.5" />
                        </span>
                        <span>{finding}</span>
                      </div>
                    ))}
                  </div>

                  {project.takeaway && (
                    <div className="mt-6 p-5 sm:p-6 rounded-2xl bg-[#0B0B0C] text-white border border-white/10 relative overflow-hidden">
                      <div className="text-[11px] font-mono uppercase tracking-wider text-[#FF5A1F] font-bold mb-2 flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4" />
                        <span>Executive Takeaway</span>
                      </div>
                      <p className="text-neutral-200 text-sm sm:text-base leading-relaxed font-medium">
                        "{project.takeaway}"
                      </p>
                    </div>
                  )}

                  <div className="pt-4 border-t border-black/5">
                    <p className="text-xs text-neutral-500 italic leading-relaxed">
                      * This case study is intended for cybersecurity awareness and defensive education. It analyzes potential runtime exposure risks and mitigation best practices without providing instructions for exploiting vulnerable systems.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column: Sticky Sidebar Specs & Conversion */}
            <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-28">
              {/* Tech Stack Spec Card */}
              <div className="rounded-3xl p-6 sm:p-7 bg-white border border-black/10 shadow-sm">
                <div className="flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-[#FF5A1F] mb-4">
                  <Cpu className="w-4 h-4" />
                  <span>Technologies Deployed</span>
                </div>

                <div className="flex flex-wrap gap-2">
                  {(Array.isArray(project.tech) ? project.tech : []).map((t) => (
                    <span
                      key={t}
                      className="px-3 py-1.5 rounded-lg text-xs font-mono font-medium bg-neutral-100 text-neutral-800 border border-black/5"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-6 pt-5 border-t border-black/5 space-y-3 text-xs text-neutral-600">
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-400">Architecture:</span>
                    <span className="font-semibold text-neutral-800">Cloud / Microservices</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-400">Security SLA:</span>
                    <span className="font-semibold text-emerald-600">99.99% Uptime Verified</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-400">Engineering:</span>
                    <span className="font-semibold text-neutral-800">NeuraVolt Core Team</span>
                  </div>
                </div>
              </div>

              {/* Client Review (if matched) */}
              {clientReview && (
                <div className="rounded-3xl p-6 sm:p-7 bg-gradient-to-br from-neutral-900 to-[#0B0B0C] text-white border border-white/10 shadow-xl">
                  <div className="flex items-center gap-1 mb-3 text-[#FF5A1F]">
                    {Array.from({ length: clientReview.rating || 5 }).map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-current" />
                    ))}
                  </div>

                  <p className="text-xs sm:text-sm text-neutral-200 italic leading-relaxed mb-4">
                    "{clientReview.quote}"
                  </p>

                  <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                    <div className="w-9 h-9 rounded-full bg-[#FF5A1F]/20 text-[#FF5A1F] font-bold text-xs grid place-items-center uppercase border border-[#FF5A1F]/40 shrink-0">
                      {clientReview.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-xs sm:text-sm text-white">
                        {clientReview.name}
                      </div>
                      <div className="text-[11px] text-neutral-400">
                        {clientReview.role}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Direct Consultation Action Card */}
              <div className="rounded-3xl p-7 bg-[#0B0B0B] text-white shadow-xl shadow-[#FF5A1F]/25 flex flex-col justify-between relative overflow-hidden border border-white/10">
                {/* Background Image: sharp team collaboration meeting photo without blur */}
                <div className="absolute inset-0 z-0">
                  <img
                    src="/brand/cta-meeting-bg.jpg"
                    alt="NeuraVolt Strategy & Engineering Team"
                    className="w-full h-full object-cover object-center"
                  />
                  {/* Contrast overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/75 to-black/90" />
                  <div className="absolute inset-0 bg-[#FF5A1F]/15" />
                </div>

                <div className="relative z-10">
                  <div className="text-xs font-mono uppercase tracking-wider text-[#FF5A1F] font-bold mb-2 flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5" /> Start Your Project
                  </div>
                  <h4 className="font-display text-xl font-bold mb-2 text-white">
                    Want to build a platform like {project.title}?
                  </h4>
                  <p className="text-neutral-300 text-xs sm:text-sm leading-relaxed mb-6">
                    Our senior engineering leads will evaluate your architecture, scoping, and roadmap on a 30-minute discovery call.
                  </p>
                </div>

                <div className="relative z-10">
                  <button
                    onClick={() => openConsultationModal(`Similar architecture to ${project.title}`)}
                    className="w-full py-3.5 px-5 rounded-2xl bg-[#FF5A1F] text-white font-semibold text-xs sm:text-sm hover:bg-[#ff6d38] transition-all shadow-md inline-flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <span>Schedule Technical Discovery</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Related Case Studies Grid */}
          {related.length > 0 && (
            <div className="pt-16 border-t border-black/10">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-[#FF5A1F] block mb-1">
                    PORTFOLIO ARCHIVES
                  </span>
                  <h3 className="font-display text-2xl font-bold text-neutral-900">
                    Explore Related Case Studies
                  </h3>
                </div>

                <Link
                  to="/portfolio"
                  className="text-xs font-semibold text-[#FF5A1F] hover:underline inline-flex items-center gap-1"
                >
                  <span>View All Projects</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {related.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/portfolio/${p.slug}`}
                    className="group block rounded-2xl overflow-hidden bg-white border border-black/10 shadow-sm hover:shadow-xl hover:border-[#FF5A1F]/40 transition-all p-4"
                  >
                    <div className="rounded-xl overflow-hidden aspect-[16/10] bg-neutral-100 mb-4">
                      <img
                        src={p.img}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </div>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h4 className="font-heading font-bold text-base text-neutral-900 group-hover:text-[#FF5A1F] transition-colors">
                        {p.title}
                      </h4>
                      <ArrowUpRight className="w-4 h-4 text-neutral-400 group-hover:text-[#FF5A1F] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </div>
                    <p className="text-xs text-neutral-500 line-clamp-2">
                      {p.tagline || p.desc}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <CTA />
    </Layout>
  );
}
